import { useOnlineStatus } from '@shared/hooks';
import { OfflineNoticeCard } from './offline-notice-card';

export interface OfflineNoticeProps {
  /** 補充說明，見 {@link OfflineNoticeCard} */
  description?: string;
  /**
   * 位置覆寫。預設貼齊視窗底部，與 `InstallPrompt` 同一個位置。
   *
   * 兩件事會需要覆寫，而且往往要一起處理：
   *
   * - **畫面底部有浮動按鈕**——卡片會蓋住它，往上抬到按鈕之上。
   * - **可能同時出現安裝提示**——兩張卡片預設位置相同，會疊在一起。
   *   安裝卡片會把自己佔掉的高度發佈成 `--install-prompt-inset`
   *   （見 `InstallPromptCard`），讀它就能疊在上面，它消失時變數會被移除，
   *   自動落回 fallback。
   *
   * @example 兩件事都要處理（diet：FAB 在 bottom 1.5rem、高 3.25rem）
   * className="bottom-[calc(var(--install-prompt-inset,calc(5.5rem+env(safe-area-inset-bottom)))+0.75rem)]"
   */
  className?: string;
}

/**
 * 沒有網路時浮出的提示，連線恢復後自動消失。
 *
 * 外觀刻意與 `InstallPrompt` 同一種浮動卡片：使用者在同一個位置、同一種樣子裡
 * 讀到的，就該是同一類「app 想告訴你一件事」的訊息。
 *
 * **它是提示，不是防線。**背後的 `navigator.onLine` 只知道有沒有網路介面
 * （見 `useOnlineStatus`），連上一個沒有出口的 Wi-Fi 時仍然是「線上」。真正要擋住
 * 送不出去的寫入，必須由請求路徑自己處理，這張卡片只負責讓失敗變得可以理解。
 *
 * @example
 * <OfflineNotice description="新增與修改需要連線才能儲存。" />
 */
export function OfflineNotice({ description, className }: OfflineNoticeProps) {
  const isOnline = useOnlineStatus();

  if (isOnline) return null;

  return <OfflineNoticeCard description={description} className={className} />;
}
